import React, { PropsWithChildren, useEffect, useState } from "react";
import {
  LiveTrackTokenApiFactory,
  LiveTrackVehicleDto,
} from "../../../common/data/service-proxies";
import { parseJwt } from "../../../common/util/parseJwt";

interface Props {
  vehicleKey?: string;
  tenantId?: string;
  setConnectionToken: React.Dispatch<React.SetStateAction<string | undefined>>;
  setResolvedVehicleId: React.Dispatch<
    React.SetStateAction<number | undefined>
  >;
  setError: React.Dispatch<React.SetStateAction<string | undefined>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setVehicle: React.Dispatch<
    React.SetStateAction<LiveTrackVehicleDto | undefined>
  >;
}

export const AnonymousLocationLiveTrack = ({
  vehicleKey,
  tenantId,
  children,
  setConnectionToken,
  setResolvedVehicleId,
  setError,
  setLoading,
  setVehicle,
}: PropsWithChildren<Props>) => {
  const [hubAccessToken, setHubAccessToken] = useState<string>();
  const [requestedVehicleId, setRequestedVehicleId] = useState<number>();
  const apiUrl =
    process.env["REACT_APP_API_URL"]?.replace(/\/+$/, "") ??
    "https://localhost:44327";

  useEffect(() => {
    if (!hubAccessToken || !requestedVehicleId) {
      return;
    }

    LiveTrackTokenApiFactory(
      undefined,
      apiUrl
    )
      .apiAppLivetracktokenIdVehicleforlivetrackGet(requestedVehicleId, {
        headers: { Authorization: "Bearer " + hubAccessToken },
      })
      .then((response) => {
        if (response.data) {
          setVehicle(response.data);
        }
      })
      .catch((error) => {
        console.error("Unable to get the requested vehicle data", error);
        setError(
          !!error?.message ? error.message.toString() : error?.toString()
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [apiUrl, hubAccessToken, requestedVehicleId, setError, setLoading, setVehicle]);

  useEffect(() => {
    setLoading(true);
    if (vehicleKey && tenantId) {
      LiveTrackTokenApiFactory(
        undefined,
        apiUrl
      )
        .apiTenantIdIdGet(tenantId, vehicleKey)
        .then((result) => {
          const token = result.data?.token;
          if (!token) {
            throw new Error("Unable to get the live tracking connection token.");
          }

          const parsed = parseJwt(token);
          const nextVehicleId = parsed?.sub ? Number(parsed.sub) : undefined;
          if (!nextVehicleId || Number.isNaN(nextVehicleId)) {
            throw new Error("Unable to resolve the live tracking vehicle.");
          }

          setHubAccessToken(token);
          setRequestedVehicleId(nextVehicleId);
          setConnectionToken(token);
          setResolvedVehicleId(nextVehicleId);
        })
        .catch((error) => {
          setLoading(false);
          setError(!!error?.message ? error.message.toString() : error?.toString());
        });
      return;
    }

    setLoading(false);
  }, [
    apiUrl,
    setConnectionToken,
    setError,
    setLoading,
    setResolvedVehicleId,
    tenantId,
    vehicleKey,
  ]);

  useEffect(() => {
    return () => {
      setConnectionToken(undefined);
      setResolvedVehicleId(undefined);
    };
  }, [setConnectionToken, setResolvedVehicleId]);

  return <>{children}</>;
};
